import * as THREE from 'three';

/**
 * Uniform-grid spatial hash for radius queries against many moving items.
 * Trades a per-frame rebuild for O(k) lookups instead of O(n) scans —
 * pays off when there are hundreds of items but only a handful are
 * actually within reach of any given query.
 *
 * The grid is unbounded (negative coordinates work fine) and lives in a
 * single Map keyed by packed cell coordinates, so memory scales with the
 * number of *occupied* cells rather than world size.
 */
export class SpatialHash<T> {
  private readonly cells = new Map<number, T[]>();
  private readonly cellSize: number;
  private readonly invCellSize: number;

  constructor(cellSize: number) {
    this.cellSize = cellSize;
    this.invCellSize = 1 / cellSize;
  }

  clear(): void {
    // Reuse arrays — clearing them is cheaper than reallocating every frame
    // when the population stays roughly constant across rebuilds.
    for (const list of this.cells.values()) list.length = 0;
  }

  insert(item: T, position: THREE.Vector3): void {
    const key = this.cellKey(position.x, position.y, position.z);
    let bucket = this.cells.get(key);
    if (!bucket) {
      bucket = [];
      this.cells.set(key, bucket);
    }
    bucket.push(item);
  }

  /** Append every item whose cell is within `radius` of `center` to `out`.
   *  False positives (items just outside `radius`) are expected — the caller
   *  must do the precise distance check. The win is going from O(n) to O(k). */
  queryRadius(center: THREE.Vector3, radius: number, out: T[]): T[] {
    const minX = Math.floor((center.x - radius) * this.invCellSize);
    const maxX = Math.floor((center.x + radius) * this.invCellSize);
    const minY = Math.floor((center.y - radius) * this.invCellSize);
    const maxY = Math.floor((center.y + radius) * this.invCellSize);
    const minZ = Math.floor((center.z - radius) * this.invCellSize);
    const maxZ = Math.floor((center.z + radius) * this.invCellSize);

    for (let ix = minX; ix <= maxX; ix++) {
      for (let iy = minY; iy <= maxY; iy++) {
        for (let iz = minZ; iz <= maxZ; iz++) {
          const bucket = this.cells.get(packCell(ix, iy, iz));
          if (!bucket) continue;
          for (let i = 0; i < bucket.length; i++) out.push(bucket[i]!);
        }
      }
    }
    return out;
  }

  private cellKey(x: number, y: number, z: number): number {
    return packCell(
      Math.floor(x * this.invCellSize),
      Math.floor(y * this.invCellSize),
      Math.floor(z * this.invCellSize)
    );
  }
}

// Pack three signed cell indices into one integer. ±2^9 cells per axis (with
// 800u cell size, ±400 ku world) is far beyond the playable sector, and one
// numeric key avoids string allocation in the hot insert/query path.
const CELL_BITS = 10;
const CELL_MASK = (1 << CELL_BITS) - 1;
const CELL_BIAS = 1 << (CELL_BITS - 1);

function packCell(ix: number, iy: number, iz: number): number {
  const x = (ix + CELL_BIAS) & CELL_MASK;
  const y = (iy + CELL_BIAS) & CELL_MASK;
  const z = (iz + CELL_BIAS) & CELL_MASK;
  return (x << (CELL_BITS * 2)) | (y << CELL_BITS) | z;
}
